import React from 'react'
import CustomSelect from '../../../../../pages/ProjectDocumentation/components/CustomSelect.tsx'
import type {
  DocumentationSlots,
  DraftMetadataFileEntry,
  RubroRow,
  SkillOption
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  clampIsoDateToMax,
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotsHavePages,
  isRubroFallbackOtrosKind,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  rubroHasValidSelectedSkill
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import BudgetSheetCompactEntry from './BudgetSheetCompactEntry.tsx'
import RubroDocumentationBlock from './RubroDocumentationBlock.tsx'
import styles from './ProjectAutofillCard.module.scss'
import {
  isRubroFuzzyKind,
  isRubroUnresolvedKind,
  PUBLISHED_RUBRO_EDIT_HINT,
  rubroFallbackOtrosReview,
  rubroUnresolvedNeedsPick
} from './projectAutofillCard/projectAutofillCardUtils.ts'

export type ProjectAutofillDefaultRubroCardProps = {
  rk: string
  rid: string
  idx: number
  sp: RubroRow
  highlighted: boolean
  expanded: boolean
  headline: string
  advisoryNotes: string[]
  skillOptions: SkillOption[]
  draftLocked: boolean
  draftId: string | undefined
  isSaving: boolean
  projectStartDate: string
  projectEndDate: string
  today: string
  documentationPreview: unknown
  metadataFiles: DraftMetadataFileEntry[]
  showGlobalDocBar: boolean
  docPdfUrl: string
  docPageCount: number | null | undefined
  uploadingSlotKey: string | null
  fuzzyConfirmByRubro: Record<string, boolean>
  onToggleExpand: (rid: string) => void
  onRemoveRubro: (rid: string) => void
  onRubroSkillSelect: (tempId: string, value: string, row: RubroRow) => void
  onUpdateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  onSelectRubroSlotFileId: (tempId: string, slot: keyof DocumentationSlots, fileId: string | null) => void
  onRubroSlotPagesChange: (tempId: string, slot: keyof DocumentationSlots, pages: number[]) => void
  onUploadRubroSlotFile: (tempId: string, slot: keyof DocumentationSlots, file: File) => Promise<void>
  onOpenBudgetSheetFullscreen: (tid: string) => void
  onFuzzyConfirmChange: (tempId: string, checked: boolean) => void
}

const ProjectAutofillDefaultRubroCard: React.FC<ProjectAutofillDefaultRubroCardProps> = ({
  rk,
  rid,
  idx,
  sp,
  highlighted,
  expanded,
  headline,
  advisoryNotes,
  skillOptions,
  draftLocked,
  draftId,
  isSaving,
  projectStartDate,
  projectEndDate,
  today,
  documentationPreview,
  metadataFiles,
  showGlobalDocBar,
  docPdfUrl,
  docPageCount,
  uploadingSlotKey,
  fuzzyConfirmByRubro,
  onToggleExpand,
  onRemoveRubro,
  onRubroSkillSelect,
  onUpdateRubro,
  onSelectRubroSlotFileId,
  onRubroSlotPagesChange,
  onUploadRubroSlotFile,
  onOpenBudgetSheetFullscreen,
  onFuzzyConfirmChange
}) => {
  const tempIdOrSp = sp.tempId || `sp-${idx}`

  return (
    <div
      className={
        highlighted
          ? `${styles.projectAutofillSubprojectCard} ${styles.projectAutofillSubprojectCardHighlight}`
          : styles.projectAutofillSubprojectCard
      }
    >
      <div className={styles.projectAutofillSubprojectHead}>
        <button
          type="button"
          className={styles.projectAutofillSubprojectToggle}
          aria-expanded={!!expanded}
          onClick={() => onToggleExpand(rid)}
        >
          {expanded ? 'Ocultar' : 'Ver detalle'}
        </button>
        {rubroUnresolvedNeedsPick(sp) ? (
          <div className={styles.projectAutofillSubprojectLabelBlock}>
            <span className={styles.projectAutofillRubroUnresolvedTitle}>Se detectó un rubro en el documento</span>
            <span className={styles.projectAutofillRubroUnresolvedLine}>
              No encontramos el nombre correcto en la plataforma.
            </span>
            <span className={styles.projectAutofillRubroUnresolvedLineMuted}>Elegí el rubro en la lista.</span>
          </div>
        ) : rubroFallbackOtrosReview(sp) ? (
          <div className={styles.projectAutofillSubprojectLabelBlock}>
            <span className={styles.projectAutofillRubroUnresolvedTitle}>Revisá el rubro</span>
            <span className={styles.projectAutofillRubroUnresolvedLine}>
              La coincidencia con un rubro de la plataforma no fue clara; se asignó un valor por defecto.
            </span>
            <span className={styles.projectAutofillRubroUnresolvedLineMuted}>
              Cambiá el rubro en la lista si corresponde.
            </span>
          </div>
        ) : (
          <span className={styles.projectAutofillSubprojectLabel}>{headline}</span>
        )}
        <button
          type="button"
          className={styles.projectAutofillSubprojectRemove}
          onClick={() => onRemoveRubro(rid)}
          disabled={!draftId || isSaving || draftLocked}
          title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
        >
          Quitar
        </button>
      </div>
      {advisoryNotes.map((text, i) => (
        <p key={`${rk}-adv-${i}`} className={styles.projectAutofillRubroAdvisoryNote} role="note">
          <span className={styles.projectAutofillRubroAdvisoryBadge}>Revisar</span>
          {text}
        </p>
      ))}
      {expanded && (
        <div className={`${styles.projectAutofillSubprojectBody} ${styles.projectAutofillSubprojectBodyEnter}`}>
          <div className={styles.projectAutofillRubroSelect}>
            <CustomSelect
              value={sp.selectedSkillId ? String(sp.selectedSkillId) : ''}
              onChange={(value) => onRubroSkillSelect(tempIdOrSp, value, sp)}
              label={
                rubroUnresolvedNeedsPick(sp)
                  ? 'Rubro de la plataforma'
                  : rubroFallbackOtrosReview(sp)
                    ? 'Rubro (revisar)'
                    : 'Rubro a cotizar'
              }
              outlineInvalid={
                (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ||
                (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId) ||
                isRubroFallbackOtrosKind(sp.skillMatchKind)
              }
              options={skillOptions.map((skill) => ({
                value: String(skill.id),
                label: skill.name
              }))}
              placeholder={
                rubroUnresolvedNeedsPick(sp)
                  ? 'Elegí el rubro de la lista'
                  : rubroFallbackOtrosReview(sp)
                    ? 'Revisá o cambiá el rubro de la lista'
                    : 'Elegir rubro de la lista'
              }
              searchable={true}
              searchPlaceholder="Buscar rubro..."
              disabled={draftLocked}
              title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
            />
          </div>
          <textarea
            value={sp.description || ''}
            onChange={(e) => onUpdateRubro(tempIdOrSp, { description: e.target.value })}
            placeholder="Describe brevemente el trabajo a realizar..."
            disabled={draftLocked}
            title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
          />

          {(() => {
            const slots = normalizeDocumentationSlots(sp.documentationSlots)
            const idsNorm = normalizeDocumentationFileIds(sp.documentationFileIds ?? {})
            const hasSlotFileId = DOCUMENTATION_SLOT_KEYS.some((k) => !!idsNorm[k])
            const showRubroDoc =
              documentationPreview != null ||
              documentationSlotsHavePages(slots) ||
              (sp.sourcePages?.length ?? 0) > 0 ||
              hasSlotFileId ||
              (draftId != null && metadataFiles.length > 0) ||
              showGlobalDocBar
            const planillaSlot = ({ revealBudgetDocSource }: { revealBudgetDocSource: () => void }) => (
              <BudgetSheetCompactEntry
                embedded
                tempId={rid}
                rubro={sp}
                draftId={draftId}
                draftLocked={draftLocked}
                isSaving={isSaving}
                updateRubro={onUpdateRubro}
                onOpenFullscreen={onOpenBudgetSheetFullscreen}
                onDeclineAgentPlanilla={() => {
                  onUpdateRubro(rid, { budgetSheetState: undefined })
                  revealBudgetDocSource()
                }}
              />
            )
            if (!showRubroDoc) {
              return (
                <BudgetSheetCompactEntry
                  tempId={rid}
                  rubro={sp}
                  draftId={draftId}
                  draftLocked={draftLocked}
                  isSaving={isSaving}
                  updateRubro={onUpdateRubro}
                  onOpenFullscreen={onOpenBudgetSheetFullscreen}
                />
              )
            }
            return (
              <RubroDocumentationBlock
                tempId={rid}
                slots={slots}
                sourcePages={sp.sourcePages}
                pdfBaseUrl={docPdfUrl || undefined}
                maxPage={docPageCount ?? undefined}
                fileIds={idsNorm}
                draftFiles={metadataFiles}
                disabled={!draftId || isSaving || draftLocked}
                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                uploadingSlotKey={uploadingSlotKey}
                onSelectSlotFileId={(slot, fileId) => onSelectRubroSlotFileId(rid, slot, fileId)}
                onSlotPagesChange={
                  docPageCount != null && draftId && !isSaving && !draftLocked
                    ? (slot, pages) => onRubroSlotPagesChange(rid, slot, pages)
                    : undefined
                }
                onUploadSlotFile={(slot, file) => onUploadRubroSlotFile(rid, slot, file)}
                budgetPlanillaSlot={planillaSlot}
                budgetSheetPresent={!!sp.budgetSheetState}
              />
            )
          })()}

          {(sp.name || sp.link) && (
            <div className={styles.projectAutofillSubprojectExtras}>
              {sp.name && <span>Nombre: {sp.name}</span>}
              {sp.link && (
                <a href={sp.link} target="_blank" rel="noopener noreferrer" className={styles.projectAutofillSubprojectLink}>
                  Enlace
                </a>
              )}
            </div>
          )}

          {isRubroFuzzyKind(sp.skillMatchKind) && (
            <label className={styles.projectAutofillFuzzyConfirm}>
              <input
                type="checkbox"
                checked={!!fuzzyConfirmByRubro[tempIdOrSp]}
                disabled={draftLocked}
                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                onChange={(e) => onFuzzyConfirmChange(tempIdOrSp, e.target.checked)}
              />
              <span>Confirmo que el rubro elegido es el correcto</span>
            </label>
          )}
          <div className={styles.projectAutofillRubroDates}>
            <div className={styles.projectAutofillRubroDatesHead}>
              <span className={styles.projectAutofillRubroDatesBlockTitle}>Recepción de presupuestos</span>
              {sp.datesInheritedFromProject === true ? (
                <span
                  className={styles.projectAutofillDatesChip}
                  title="Las fechas coinciden con las del proyecto hasta que las cambies."
                >
                  Como el proyecto
                </span>
              ) : null}
            </div>
            <label className={styles.projectAutofillRubroDateLabel}>
              <span>Fecha inicio de recepción de presupuestos</span>
              <input
                type="date"
                value={sp.startDate || ''}
                min={projectStartDate || today}
                max={projectEndDate || undefined}
                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                onChange={(e) => {
                  let nextStart = e.target.value
                  if (projectEndDate && nextStart) nextStart = clampIsoDateToMax(nextStart, projectEndDate)
                  let nextEnd = sp.endDate
                  if (projectEndDate && nextEnd) nextEnd = clampIsoDateToMax(nextEnd, projectEndDate)
                  if (nextEnd && nextStart && nextEnd < nextStart) {
                    nextEnd = nextStart
                  }
                  onUpdateRubro(tempIdOrSp, {
                    startDate: nextStart,
                    endDate: nextEnd,
                    datesInheritedFromProject: false
                  })
                }}
                disabled={draftLocked}
              />
            </label>
            <label className={styles.projectAutofillRubroDateLabel}>
              <span>Fecha fin de recepción de presupuestos</span>
              <input
                type="date"
                value={sp.endDate || ''}
                min={sp.startDate || projectStartDate || today}
                max={projectEndDate || undefined}
                title={draftLocked ? PUBLISHED_RUBRO_EDIT_HINT : undefined}
                onChange={(e) => {
                  let v = e.target.value
                  if (projectEndDate && v) v = clampIsoDateToMax(v, projectEndDate)
                  const s = sp.startDate
                  if (s && v && v < s) v = s
                  onUpdateRubro(tempIdOrSp, {
                    endDate: v,
                    datesInheritedFromProject: false
                  })
                }}
                disabled={draftLocked}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectAutofillDefaultRubroCard
